import "./App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "./contexts/ThemeContext";
import { CelebrationProvider } from "./contexts/CelebrationContext";
import { CelebrationOverlay } from "./components/common/CelebrationOverlay";
import { AppDataProvider } from "./contexts/AppDataContext";
import { DeskDataProvider } from "./contexts/DeskDataContext";
import { AppShell } from "./components/shell/AppShell";
import { DeskShell } from "./components/desk/DeskShell";
import { ControlPlaneShell } from "./components/control/ControlPlaneShell";
import { Toaster } from "./components/ui/sonner";
import Login from "./pages/Login";
import KairooLogin from "./pages/KairooLogin";
import OwnerCockpit from "./pages/OwnerCockpit";
import Members from "./pages/Members";
import OwnerMemberDetail from "./pages/MemberDetail";
import Leads from "./pages/Leads";
import Marketing from "./pages/Marketing";
import Staff from "./pages/Staff";
import Branches from "./pages/Branches";
import Plans from "./pages/Plans";
import Money from "./pages/Money";
import RevenueExtras from "./pages/RevenueExtras";
import Operations from "./pages/Operations";
import Reports from "./pages/Reports";
import OwnerRetail from "./pages/Retail";
import { ModuleGate } from "./components/shell/ModuleGate";
import DeskToday from "./pages/desk/DeskToday";
import DeskShiftStart from "./pages/desk/DeskShiftStart";
import MemberLookup from "./pages/desk/MemberLookup";
import WalkInConversion from "./pages/desk/WalkInConversion";
import MemberDetail from "./pages/desk/MemberDetail";
import CheckIn from "./pages/desk/CheckIn";
import MoneyIn from "./pages/desk/MoneyIn";
import Retail from "./pages/desk/Retail";
import DeskLeads from "./pages/desk/Leads";
import FloorOps from "./pages/desk/FloorOps";
import { MgrLayout } from "./components/desk/MgrLayout";
import BranchDashboard from "./pages/desk/mgr/BranchDashboard";
import Expenses from "./pages/desk/mgr/Expenses";
import PayoutRun from "./pages/desk/mgr/PayoutRun";
import RefundApproval from "./pages/desk/mgr/RefundApproval";
import DayEnd from "./pages/desk/mgr/DayEnd";
import Roster from "./pages/desk/mgr/Roster";
import TrainerPerformance from "./pages/desk/mgr/TrainerPerformance";
import LocalOffers from "./pages/desk/mgr/LocalOffers";
import StockAdjustment from "./pages/desk/mgr/StockAdjustment";
import Governance from "./pages/desk/mgr/Governance";
import MemberLogin from "./pages/member/MemberLogin";
import { MemberShell } from "./components/member/MemberShell";
import { MemberDataProvider } from "./contexts/MemberDataContext";
import MemberHome from "./pages/member/Home";
import MemberBook from "./pages/member/Book";
import MemberMembership from "./pages/member/Membership";
import MemberProgress from "./pages/member/Progress";
import MemberShop from "./pages/member/Shop";
import MemberMore from "./pages/member/More";
import MemberPay from "./pages/member/Pay";
import MemberProfile from "./pages/member/Profile";
import { TrainerDataProvider } from "./contexts/TrainerDataContext";
import { TrainerShell } from "./components/trainer/TrainerShell";
import TrainerToday from "./pages/trainer/Today";
import TrainerSchedule from "./pages/trainer/Schedule";
import TrainerAvailability from "./pages/trainer/Availability";
import TrainerClients from "./pages/trainer/Clients";
import TrainerClientDetail from "./pages/trainer/ClientDetail";
import TrainerEarnings from "./pages/trainer/Earnings";
import TrainerAppPerformance from "./pages/trainer/Performance";
import TrainerMessage from "./pages/trainer/Message";
import ControlDashboard from "./pages/control/ControlDashboard";
import GymsList from "./pages/control/GymsList";
import GymDetail from "./pages/control/GymDetail";
import TrialManagement from "./pages/control/TrialManagement";
import MonetizationDashboard from "./pages/control/MonetizationDashboard";
import IntegrationsConsole from "./pages/control/IntegrationsConsole";
import KairooStaffPage from "./pages/control/KairooStaffPage";
import EmulatorEntry from "./pages/control/EmulatorEntry";
import EmulateUnavailable from "./pages/EmulateUnavailable";
import Settings from "./pages/Settings";
import StubPage from "./pages/StubPage";
import MigrationHome from "./pages/migration/MigrationHome";
import MigrationWizard from "./pages/migration/MigrationWizard";
import MigrationTools from "./pages/migration/MigrationTools";

const STUB_ROUTES = [
  ["help", "Help"],
];

// UX-CP-03: Emulator is now a real screen (see below). Platform Ops/BroadcastOS admin remain
// out of scope this prompt, still on the shared StubPage.
const CONTROL_STUB_ROUTES = [
  ["platform-ops", "Platform Ops"], ["broadcast-os", "BroadcastOS"],
];

function AppRoutes() {
  return (
    <AppDataProvider>
      <Routes>
        <Route path="/" element={<AppShell />}>
          <Route index element={<Navigate to="/app/dashboard" replace />} />
          <Route path="dashboard" element={<OwnerCockpit />} />
          <Route path="members" element={<Members />} />
          <Route path="members/:memberId" element={<OwnerMemberDetail />} />
          <Route path="leads" element={<Leads />} />
          <Route path="marketing" element={<Marketing />} />
          <Route path="staff" element={<Staff />} />
          <Route path="branches" element={<Branches />} />
          <Route path="plans" element={<Plans />} />
          <Route path="money" element={<Money />} />
          <Route path="revenue-extras" element={<ModuleGate module="loyalty" title="Revenue Extras"><RevenueExtras /></ModuleGate>} />
          <Route path="retail" element={<ModuleGate module="retail" title="Retail"><OwnerRetail /></ModuleGate>} />
          <Route path="operations" element={<Operations />} />
          <Route path="reports" element={<Reports />} />
          <Route path="settings" element={<Settings />} />
          <Route path="migration" element={<MigrationHome />} />
          <Route path="migration/tools" element={<MigrationTools />} />
          <Route path="migration/wizard/:batchId" element={<MigrationWizard />} />
          {STUB_ROUTES.map(([key, title]) => (
            <Route key={key} path={key} element={<StubPage title={title} />} />
          ))}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </AppDataProvider>
  );
}

// UX-FD-01: a separate route namespace + shell for Front Desk staff — see DeskDataContext's own
// comment for why this is deliberately not folded into AppRoutes/AppShell.
function DeskRoutes() {
  return (
    <DeskDataProvider>
      <Routes>
        <Route path="/" element={<DeskShell />}>
          <Route index element={<Navigate to="/desk/today" replace />} />
          <Route path="today" element={<DeskToday />} />
          <Route path="shift" element={<DeskShiftStart />} />
          <Route path="members" element={<MemberLookup />} />
          <Route path="members/new" element={<WalkInConversion />} />
          <Route path="members/:memberId" element={<MemberDetail />} />
          <Route path="checkin" element={<CheckIn />} />
          <Route path="money" element={<MoneyIn />} />
          <Route path="retail" element={<Retail />} />
          <Route path="leads" element={<DeskLeads />} />
          <Route path="floor-ops" element={<FloorOps />} />
          <Route path="mgr" element={<MgrLayout />}>
            <Route index element={<BranchDashboard />} />
            <Route path="expenses" element={<Expenses />} />
            <Route path="payouts" element={<PayoutRun />} />
            <Route path="refunds" element={<RefundApproval />} />
            <Route path="day-end" element={<DayEnd />} />
            <Route path="roster" element={<Roster />} />
            <Route path="performance" element={<TrainerPerformance />} />
            <Route path="offers" element={<LocalOffers />} />
            <Route path="stock" element={<StockAdjustment />} />
            <Route path="governance" element={<Governance />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </DeskDataProvider>
  );
}

function ControlRoutes() {
  return (
    <Routes>
      <Route path="/" element={<ControlPlaneShell />}>
        <Route index element={<Navigate to="/kairoo/dashboard" replace />} />
        <Route path="dashboard" element={<ControlDashboard />} />
        <Route path="gyms" element={<GymsList />} />
        <Route path="gyms/:orgId" element={<GymDetail />} />
        <Route path="trials" element={<TrialManagement />} />
        <Route path="monetization" element={<MonetizationDashboard />} />
        <Route path="integrations" element={<IntegrationsConsole />} />
        <Route path="staff" element={<KairooStaffPage />} />
        <Route path="emulator" element={<EmulatorEntry />} />
        {CONTROL_STUB_ROUTES.map(([key, title]) => (
          <Route key={key} path={key} element={<StubPage title={title} />} />
        ))}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

// Safety net (testing_agent_v4 iteration 17): an unmatched nested path must never render a
// blank Outlet — always the shell chrome + a plain message, so a stale/typo'd deep link degrades
// gracefully instead of a white screen.
function NotFound() {
  return (
    <div className="solid-surface rounded-[14px] p-16 flex flex-col items-center text-center max-w-2xl" data-testid="not-found-page">
      <p className="font-bold text-lg" style={{ color: "var(--scarlet-text)" }}>Page not found</p>
      <p className="text-sm mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>That link doesn't match any page here.</p>
    </div>
  );
}

function MemberRoutes() {
  return (
    <MemberDataProvider>
      <Routes>
        <Route path="/" element={<MemberShell />}>
          <Route index element={<Navigate to="/member/home" replace />} />
          <Route path="home" element={<MemberHome />} />
          <Route path="book" element={<MemberBook />} />
          <Route path="membership" element={<MemberMembership />} />
          <Route path="pay" element={<MemberPay />} />
          <Route path="progress" element={<MemberProgress />} />
          <Route path="shop" element={<MemberShop />} />
          <Route path="more" element={<MemberMore />} />
          <Route path="profile" element={<MemberProfile />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </MemberDataProvider>
  );
}

// UX-TR-01: the Trainer App — its own persona route namespace, same pattern as DeskRoutes/
// MemberRoutes above (dedicated data context + shell, never sharing AppShell/DeskShell).
function TrainerRoutes() {
  return (
    <TrainerDataProvider>
      <Routes>
        <Route path="/" element={<TrainerShell />}>
          <Route index element={<Navigate to="/trainer/today" replace />} />
          <Route path="today" element={<TrainerToday />} />
          <Route path="schedule" element={<TrainerSchedule />} />
          <Route path="availability" element={<TrainerAvailability />} />
          <Route path="clients" element={<TrainerClients />} />
          <Route path="clients/:memberId" element={<TrainerClientDetail />} />
          <Route path="earnings" element={<TrainerEarnings />} />
          <Route path="performance" element={<TrainerAppPerformance />} />
          <Route path="message" element={<TrainerMessage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </TrainerDataProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <CelebrationProvider>
        <div className="App">
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/login" element={<Login />} />
              <Route path="/kairoo/login" element={<KairooLogin />} />
              <Route path="/emulate/unavailable" element={<EmulateUnavailable />} />
              <Route path="/member/login" element={<MemberLogin />} />
              <Route path="/app/*" element={<AppRoutes />} />
              <Route path="/desk/*" element={<DeskRoutes />} />
              <Route path="/kairoo/*" element={<ControlRoutes />} />
              <Route path="/member/*" element={<MemberRoutes />} />
              <Route path="/trainer/*" element={<TrainerRoutes />} />
            </Routes>
          </BrowserRouter>
          <Toaster />
          <CelebrationOverlay />
        </div>
      </CelebrationProvider>
    </ThemeProvider>
  );
}

export default App;
